import { ArrowLeft, CheckCircle } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';

import { useSalesforceMappings } from '../hooks/useSalesforceMappings';
import {
  type AhoyPropertyOption,
  SALESFORCE_OBJECT_TYPES,
  type SalesforceFieldMapping,
  type SalesforceObjectType,
} from '../types';
import type { MappingUpdate } from './EditableMappingRow';
import { MappingSections } from './MappingSections';

interface SalesforceFieldMappingManagerProps {
  orgId: number | string;
  availablePropertiesByObject: Record<
    SalesforceObjectType,
    AhoyPropertyOption[]
  >;
  connected: boolean;
  onBack?: () => void;
}

const TABS = SALESFORCE_OBJECT_TYPES.map(o => ({ id: o.id, label: o.label }));
const EMPTY_MAPPINGS: SalesforceFieldMapping[] = [];

export function SalesforceFieldMappingManager({
  orgId,
  availablePropertiesByObject,
  connected,
  onBack,
}: SalesforceFieldMappingManagerProps) {
  const { mappings, updateMapping } = useSalesforceMappings(orgId, {
    enabled: connected,
  });

  const [activeTab, setActiveTab] = useState<SalesforceObjectType>('account');
  const handleTabChange = useCallback(
    (id: string) => setActiveTab(id as SalesforceObjectType),
    []
  );

  const allMappings = useMemo(
    () => mappings.data?.mappings ?? EMPTY_MAPPINGS,
    [mappings.data]
  );

  const visibleMappings = useMemo(
    () => allMappings.filter(m => m.object_type === activeTab),
    [allMappings, activeTab]
  );

  const availableAhoyProperties = availablePropertiesByObject[activeTab];

  const handleUpdate = useCallback(
    (mappingId: number, body: MappingUpdate) =>
      updateMapping.mutate({ mappingId, body }),
    [updateMapping]
  );

  const isMutating = updateMapping.isPending;
  const isMutateSuccess = updateMapping.isSuccess;
  let saveStatus = null;
  if (isMutating) {
    saveStatus = (
      <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-secondary)]'>
        <LoadingIndicator size='xs' inline />
        Saving…
      </span>
    );
  } else if (isMutateSuccess) {
    saveStatus = (
      <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-success-primary)]'>
        <CheckCircle className='h-4 w-4' aria-hidden='true' />
        All changes saved
      </span>
    );
  }

  return (
    <div className='space-y-5'>
      <div className='flex items-start justify-between gap-4'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          Field mappings
        </h3>
        {onBack && (
          <Button
            color='secondary'
            size='sm'
            onClick={onBack}
            iconLeading={ArrowLeft}
          >
            Back
          </Button>
        )}
      </div>

      <HorizontalTabs
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {!mappings.isLoading && !mappings.error && allMappings.length > 0 && (
        <div className='flex items-center justify-end gap-3'>
          <div className='min-h-[20px]'>{saveStatus}</div>
        </div>
      )}

      {updateMapping.isError && (
        <AlertFloating
          color='error'
          title="Couldn't save your last change"
          description='Please try again.'
        />
      )}

      {mappings.isLoading && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Loading field mappings…
        </p>
      )}

      {mappings.error && !mappings.isLoading && (
        <AlertFloating
          color='error'
          title="We couldn't load field mappings"
          description='Please refresh and try again.'
        />
      )}

      {!mappings.isLoading && !mappings.error && visibleMappings.length > 0 && (
        <MappingSections
          mappings={visibleMappings}
          availableAhoyProperties={availableAhoyProperties}
          isMutating={isMutating}
          onUpdate={handleUpdate}
        />
      )}

      {!mappings.isLoading &&
        !mappings.error &&
        visibleMappings.length === 0 && (
          <p className='text-sm text-[var(--color-text-secondary)]'>
            No field mappings found for this object type.
          </p>
        )}
    </div>
  );
}
